import type { AspectRatio } from '@lumen5/luminary';
import { createAction } from '@reduxjs/toolkit';

import type {
    DisplayGroup,
    SceneAttributesRecommendation,
    SceneDesign,
    SceneDesignIdsPerTemplate,
    Template,
    TemplateCategory,
    TemplateStatusEnum,
} from 'js/src/client';
import type { PaletteData } from 'js/src/data/templates/templates-types';
import { TEMPLATES_ACTIONS } from 'js/src/data/templates/templates-types';
import type { ImmutableMap, TemplateCategoryId } from 'js/src/libs/consts';

export const storeTemplateCategories = createAction<{
    categories: TemplateCategory[];
}>(TEMPLATES_ACTIONS.STORE_TEMPLATE_CATEGORIES);

export const storeTemplates = createAction<{
    templates: Template[];
}>(TEMPLATES_ACTIONS.STORE_TEMPLATES);

export const storeTemplate = createAction<{
    template: Template;
}>(TEMPLATES_ACTIONS.STORE_TEMPLATE);

export const setTemplateStatus = createAction<{
    templateId: string;
    status: TemplateStatusEnum;
}>(TEMPLATES_ACTIONS.SET_TEMPLATE_STATUS);

export const setTemplateColorPalettes = createAction<{
    templateId: string;
    palettes: Immutable.List<ImmutableMap<PaletteData>>;
}>(TEMPLATES_ACTIONS.SET_TEMPLATE_COLOR_PALETTES);

export const setTemplateDisplayName = createAction<{
    templateId: string;
    displayName: string;
}>(TEMPLATES_ACTIONS.SET_TEMPLATE_DISPLAY_NAME);

export const setTemplateDefaultStoryboardSceneDesign = createAction<{
    templateId: string;
    sceneDesignId: string;
}>(TEMPLATES_ACTIONS.SET_TEMPLATE_DEFAULT_STORYBOARD_SCENE_DESIGN);

export const setTemplateDefaultOutroSceneDesign = createAction<{
    templateId: string;
    sceneDesignId: string;
}>(TEMPLATES_ACTIONS.SET_TEMPLATE_DEFAULT_OUTRO_SCENE_DESIGN);

export const setTemplateDefaultIconSceneDesign = createAction<{
    templateId: string;
    sceneDesignId: string;
}>(TEMPLATES_ACTIONS.SET_TEMPLATE_DEFAULT_ICON_SCENE_DESIGN);

export const setTemplateDefaultSceneDesign = createAction<{
    templateId: string;
    sceneDesignId: string;
}>(TEMPLATES_ACTIONS.SET_TEMPLATE_DEFAULT_SCENE_DESIGN);

export const setTemplateDefaultTalkingHeadSceneDesign = createAction<{
    templateId: string;
    sceneDesignId: string;
}>(TEMPLATES_ACTIONS.SET_TEMPLATE_DEFAULT_TALKING_HEAD_SCENE_DESIGN);

export const setTemplateDefaultCutawaySceneDesign = createAction<{
    templateId: string;
    sceneDesignId: string;
}>(TEMPLATES_ACTIONS.SET_TEMPLATE_DEFAULT_CUTAWAY_SCENE_DESIGN);

export const setTemplateDefaultCaptionSceneDesign = createAction<{
    templateId: string;
    sceneDesignId: string;
}>(TEMPLATES_ACTIONS.SET_TEMPLATE_DEFAULT_CAPTION_SCENE_DESIGN);

export const setTemplateDefaultVoiceoverSceneDesign = createAction<{
    templateId: string;
    sceneDesignId: string;
}>(TEMPLATES_ACTIONS.SET_TEMPLATE_DEFAULT_VOICEOVER_SCENE_DESIGN);

export const setTemplateImageUrl = createAction<{
    templateId: string;
    imageUrl: string;
}>(TEMPLATES_ACTIONS.SET_TEMPLATE_IMAGE_URL);

export const setTemplateMp4Url = createAction<{
    templateId: string;
    mp4Url: string;
}>(TEMPLATES_ACTIONS.SET_TEMPLATE_MP4_URL);

export const setTemplateAspectRatioMp4Url = createAction<{
    templateId: string;
    aspectRatio: AspectRatio;
    mp4Url: string;
}>(TEMPLATES_ACTIONS.SET_TEMPLATE_ASPECT_RATIO_MP4_URL);

export const setTemplateTextMinSize = createAction<{
    templateId: string;
    textMinSize: number;
}>(TEMPLATES_ACTIONS.SET_TEMPLATE_TEXT_MIN_SIZE);

export const setTemplateTextMaxSize = createAction<{
    templateId: string;
    textMaxSize: number;
}>(TEMPLATES_ACTIONS.SET_TEMPLATE_TEXT_MAX_SIZE);

export const setTemplateOrders = createAction<{
    orderedTemplateIds: string[];
}>(TEMPLATES_ACTIONS.SET_TEMPLATE_ORDERS);

export const setTemplateCategoryId = createAction<{
    templateId: string;
    categoryId: number;
}>(TEMPLATES_ACTIONS.SET_TEMPLATE_CATEGORY_ID);

export const setTemplateDefaultTextSizeOptions = createAction<{
    templateId: string;
    options: Immutable.List<number>;
}>(TEMPLATES_ACTIONS.SET_TEMPLATE_DEFAULT_TEXT_SIZE_OPTIONS);

export const storeAllSceneDesignIdsPerTemplate = createAction<{
    allSceneDesignIdsPerTemplate: SceneDesignIdsPerTemplate[];
}>(TEMPLATES_ACTIONS.STORE_ALL_SCENE_DESIGN_IDS_PER_TEMPLATE);

export const storeSceneDesigns = createAction<{
    sceneDesigns: SceneDesign[];
}>(TEMPLATES_ACTIONS.STORE_SCENE_DESIGNS);

export const storeSceneDesign = createAction<{
    sceneDesign: SceneDesign;
}>(TEMPLATES_ACTIONS.STORE_SCENE_DESIGN);

export const storeDisplayGroups = createAction<{
    displayGroups: DisplayGroup[];
}>(TEMPLATES_ACTIONS.STORE_DISPLAY_GROUPS);

export const setDisplayGroupName = createAction<{
    displayGroupId: number;
    displayGroupName: string;
}>(TEMPLATES_ACTIONS.SET_DISPLAY_GROUP_NAME);

export const setSceneDesignAttributesRecommendation = createAction<{
    attributesRecommendation: SceneAttributesRecommendation;
    sceneDesignId: string;
}>(TEMPLATES_ACTIONS.SET_SCENE_DESIGN_ATTRIBUTES_RECOMMENDATION);

export const setSceneDesignData = createAction<{
    sceneDesignId: string;
    data: Partial<SceneDesign>;
}>(TEMPLATES_ACTIONS.SET_SCENE_DESIGN_DATA);

export const setNonRecommendableAssetNames = createAction<{
    sceneDesignId: string;
    nonRecommendableAssetNames: string[];
}>(TEMPLATES_ACTIONS.SET_NON_RECOMMENDABLE_ASSETS);

/**
 * Sets the template data directly to the store.
 * Be sure this is what you want to do! In the editor, you probably want `editor-operations/setTemplateData`.
 */
export const _setTemplateData = createAction<{
    templateId: string;
    data: unknown;
}>(TEMPLATES_ACTIONS.SET_TEMPLATE_DATA);

export const setSceneDesignStatus = createAction<{
    sceneDesignId: string;
    status: TemplateStatusEnum;
}>(TEMPLATES_ACTIONS.SET_SCENE_DESIGN_STATUS);

export const setSceneDesignOrders = createAction<{
    templateId: string;
    orderedSceneDesignIds: string[];
}>(TEMPLATES_ACTIONS.SET_SCENE_DESIGN_ORDERS);

export const setSceneDesignDisplayGroup = createAction<{
    sceneDesignId: string;
    displayGroupId: number;
}>(TEMPLATES_ACTIONS.SET_SCENE_DESIGN_DISPLAY_GROUP);

export const storeRecentSelectedSceneDesign = createAction<{
    sceneDesignId: string;
}>(TEMPLATES_ACTIONS.STORE_RECENT_SELECTED_SCENE_DESIGN);

export const setActiveTemplateCategoryId = createAction<{
    categoryId: TemplateCategoryId | null;
}>(TEMPLATES_ACTIONS.SET_ACTIVE_TEMPLATE_CATEGORY);

export const setTemplateColorShuffleEnabled = createAction<{
    templateId: string;
    color_shuffle_enabled: boolean;
}>(TEMPLATES_ACTIONS.SET_TEMPLATE_COLOR_SHUFFLE_ENABLED);

export const setSceneDesignDisplayName = createAction<{
    sceneDesignId: string;
    displayName: string | null;
}>(TEMPLATES_ACTIONS.SET_SCENE_DESIGN_DISPLAY_NAME);

export const setTemplateSearchQuery = createAction<{
    query: string;
}>(TEMPLATES_ACTIONS.SET_TEMPLATE_SEARCH_QUERY);
